/**
 * RefreshButton — V36.34
 *
 * Bouton "Actualiser" standardisé pour TOUS les panneaux Super Admin.
 *
 * Responsabilités :
 *  - Spinner animé (animate-spin) pendant le rechargement
 *  - Disabled anti-double-clic
 *  - Toast success/error automatique (sauf mode silent)
 *  - data-testid pour QA/E2E
 *  - Variants visuels (light/dark/ghost) pour s'adapter aux headers gradient ou neutres
 *
 * Usage minimal :
 *   <RefreshButton onRefresh={fetchAll} testId="my-section-refresh-btn" />
 *
 * Usage avancé :
 *   <RefreshButton
 *     onRefresh={async () => { await reloadAll(); }}
 *     loading={externalLoading}    // optionnel : état externe (sinon géré en interne)
 *     successMessage="3 documents rechargés"
 *     errorMessage="Erreur lors du rechargement"
 *     variant="dark"               // 'light' (default) | 'dark' | 'ghost'
 *     size="sm"                    // 'sm' | 'default'
 *     label="Actualiser"           // par defaut
 *     loadingLabel="Actualisation…"
 *     testId="my-section-refresh-btn"
 *   />
 */
import React, { useCallback, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './button';

const VARIANTS = {
  light: 'bg-white/10 hover:bg-white/20 border-white/30 text-white hover:text-white backdrop-blur-sm disabled:opacity-60',
  dark: 'bg-slate-900/5 hover:bg-slate-900/10 border-slate-300 text-slate-700 hover:text-slate-900 disabled:opacity-60',
  ghost: 'bg-transparent hover:bg-slate-100 border-slate-200 text-slate-700 disabled:opacity-60',
};

export function RefreshButton({
  onRefresh,
  loading: externalLoading,
  successMessage = 'Données actualisées',
  errorMessage = "Erreur lors de l'actualisation",
  variant = 'light',
  size = 'sm',
  label = 'Actualiser',
  loadingLabel = 'Actualisation…',
  testId = 'refresh-btn',
  className = '',
  silent = false,
}) {
  const [internalLoading, setInternalLoading] = useState(false);
  const loading = externalLoading !== undefined ? externalLoading : internalLoading;

  const handleClick = useCallback(async () => {
    if (loading) return;
    if (externalLoading === undefined) setInternalLoading(true);
    try {
      await onRefresh?.();
      if (!silent) toast.success(successMessage);
    } catch {
      if (!silent) toast.error(errorMessage);
    } finally {
      if (externalLoading === undefined) setInternalLoading(false);
    }
  }, [loading, onRefresh, externalLoading, silent, successMessage, errorMessage]);

  const variantClass = VARIANTS[variant] || VARIANTS.light;

  return (
    <Button
      variant="outline"
      size={size}
      onClick={handleClick}
      disabled={loading}
      className={`${variantClass} ${className}`}
      data-testid={testId}
    >
      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
      {loading ? loadingLabel : label}
    </Button>
  );
}

export default RefreshButton;
